/**
 * frontend/src/context/AuthContext.js
 * 
 * React Context provider for authentication state management.
 * It provides user data, loading status, and authentication methods to the entire app.
 * 
 * Common functions:
 * - login: Authenticates user with email and password.
 * - loginWithGoogle: Authenticates user using Google OAuth credential.
 * - register: Creates a new user account.
 * - logout: Clears authentication state and local storage token.
 * - useAuth: Custom hook to access auth context.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    await fetchUser();
    return response.data;
  };

  const loginWithGoogle = async (credential) => {
    const response = await authAPI.googleLogin(credential);
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    await fetchUser();
    return response.data;
  };

  const loginWithGoogleCode = async (code) => {
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    const response = await authAPI.googleCodeExchange(code, redirectUri);
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    await fetchUser();
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register({ name, email, password });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    loginWithGoogleCode,
    register,
    logout,
    refreshUser: fetchUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
